"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.defaultFuelSettings = exports.FuelLineInfoSchema = exports.FuelPosConfigSchema = exports.FuelPosGradeSchema = exports.FuelPublishSchema = exports.FuelPublishWriteSchema = exports.FuelPublishInputSchema = exports.FuelPromoSchema = exports.FuelPromoInputSchema = exports.FuelPromoKindSchema = exports.FuelLocationSettingsSchema = exports.FuelSettingsSchema = exports.FuelTillSettingsSchema = exports.FuelDraftEditsInputSchema = exports.FuelDraftEditSchema = exports.FuelPriceCellSchema = exports.FuelPriceDraftSchema = exports.FuelGradeInputSchema = exports.FuelGradeSchema = exports.FuelPriceMillsSchema = exports.FuelEntryModeSchema = exports.FUEL_FEATURE = void 0;
exports.resolveFuelPrice = resolveFuelPrice;
exports.resolveFuelDiscount = resolveFuelDiscount;
exports.computeFuelLine = computeFuelLine;
exports.formatFuelPrice = formatFuelPrice;
exports.formatGallons = formatGallons;
const zod_1 = require("zod");
/**
 * Gas & Fuel — prepaid fuel sold at the till.
 *
 * RubyTill never talks to the pumps: the cashier rings a grade + $ amount (or
 * gallons), the customer pays, and the pump is authorised from the station's
 * own console. Prices are stored in MILLS (thousandths of a dollar) so the
 * 9/10¢ on the street sign ($3.899 → 3899) is exact.
 */
exports.FUEL_FEATURE = 'pos.button.gas';
exports.FuelEntryModeSchema = zod_1.z.enum(['amount', 'gallons']);
/** Price per gallon in mills: 3899 = $3.899. Ceiling $99.999. */
exports.FuelPriceMillsSchema = zod_1.z.number().int().min(1).max(99999);
exports.FuelGradeSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    gradeId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1).max(60),
    label: zod_1.z.string().max(60).optional(),
    color: zod_1.z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#16a34a'),
    sortOrder: zod_1.z.number().int().default(0),
    active: zod_1.z.boolean().default(true),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
exports.FuelGradeInputSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(60),
    label: zod_1.z.string().max(60).optional(),
    color: zod_1.z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    sortOrder: zod_1.z.number().int().optional(),
    active: zod_1.z.boolean().optional(),
});
/** A pending (unpublished) edit to one price cell. `priceMills: null` on a
 *  location cell = go back to following the default. */
exports.FuelPriceDraftSchema = zod_1.z.object({
    priceMills: exports.FuelPriceMillsSchema.nullable(),
    hidden: zod_1.z.boolean().default(false),
});
/**
 * One cell of the grade × location matrix. `locationId: null` is the DEFAULT
 * column. On a location cell, `priceMills: null` means "follows default".
 * `hidden` on a location cell = this grade is not sold at that location.
 */
exports.FuelPriceCellSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    gradeId: zod_1.z.string().min(1),
    locationId: zod_1.z.string().nullable(),
    priceMills: exports.FuelPriceMillsSchema.nullable(),
    hidden: zod_1.z.boolean().default(false),
    draft: exports.FuelPriceDraftSchema.nullable().default(null),
    publishedAt: zod_1.z.string().nullable().default(null),
    updatedAt: zod_1.z.string(),
});
exports.FuelDraftEditSchema = zod_1.z.object({
    gradeId: zod_1.z.string().min(1),
    locationId: zod_1.z.string().nullable(),
    /** null discards the draft for this cell */
    draft: exports.FuelPriceDraftSchema.nullable(),
});
exports.FuelDraftEditsInputSchema = zod_1.z.object({
    edits: zod_1.z.array(exports.FuelDraftEditSchema).min(1).max(500),
});
/** Till behaviour. Tenant-wide defaults; a location may override any of them. */
exports.FuelTillSettingsSchema = zod_1.z.object({
    startMode: exports.FuelEntryModeSchema.default('amount'),
    /** Quick-amount buttons, in whole dollars */
    quickAmounts: zod_1.z.array(zod_1.z.number().int().min(1).max(1000)).max(8).default([10, 20, 30, 40, 50]),
    askPump: zod_1.z.boolean().default(true),
    pumpCount: zod_1.z.number().int().min(0).max(64).default(8),
    /** true = the posted price already includes fuel taxes, so no tax is added */
    taxIncluded: zod_1.z.boolean().default(true),
    /** POS tax ids applied to the fuel line when taxIncluded is false */
    taxIds: zod_1.z.array(zod_1.z.string()).default([]),
    allowRefunds: zod_1.z.boolean().default(true),
});
exports.FuelSettingsSchema = exports.FuelTillSettingsSchema.extend({
    /** Let store-wide %/$ promotions and order discounts touch fuel lines */
    allowStorePromos: zod_1.z.boolean().default(false),
    /** Only the single biggest fuel discount applies (no stacking) */
    bestDiscountOnly: zod_1.z.boolean().default(true),
});
exports.FuelLocationSettingsSchema = zod_1.z.object({
    locationId: zod_1.z.string().min(1),
    enabled: zod_1.z.boolean().default(false),
    overrides: exports.FuelTillSettingsSchema.partial().default({}),
    updatedAt: zod_1.z.string().optional(),
});
/**
 * Fuel promotions are cents-per-gallon off the pump price.
 *  - everyone: always applies
 *  - loyalty:  a customer is attached to the sale
 *  - cash:     the cashier marks the prepay as a cash sale
 */
exports.FuelPromoKindSchema = zod_1.z.enum(['everyone', 'loyalty', 'cash']);
exports.FuelPromoInputSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(80),
    kind: exports.FuelPromoKindSchema,
    centsPerGallon: zod_1.z.number().int().min(1).max(200),
    /** empty = all grades */
    gradeIds: zod_1.z.array(zod_1.z.string()).default([]),
    /** empty = all locations */
    locationIds: zod_1.z.array(zod_1.z.string()).default([]),
    active: zod_1.z.boolean().default(true),
});
exports.FuelPromoSchema = exports.FuelPromoInputSchema.extend({
    tenantId: zod_1.z.string().min(1),
    promoId: zod_1.z.string().min(1),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
exports.FuelPublishInputSchema = zod_1.z.object({
    /** grades to publish; omitted = every grade with a draft */
    gradeIds: zod_1.z.array(zod_1.z.string()).optional(),
    scope: zod_1.z.enum(['all', 'selected']),
    locationIds: zod_1.z.array(zod_1.z.string()).default([]),
    /** remove location overrides for these grades so every location matches the default */
    clearOverrides: zod_1.z.boolean().default(false),
    /** ISO time; omitted = now */
    scheduledAt: zod_1.z.string().datetime().optional(),
});
exports.FuelPublishWriteSchema = zod_1.z.object({
    gradeId: zod_1.z.string(),
    locationId: zod_1.z.string().nullable(),
    fromPriceMills: zod_1.z.number().int().nullable(),
    toPriceMills: zod_1.z.number().int().nullable(),
    fromHidden: zod_1.z.boolean(),
    toHidden: zod_1.z.boolean(),
});
exports.FuelPublishSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    publishId: zod_1.z.string().min(1),
    status: zod_1.z.enum(['scheduled', 'published', 'cancelled']),
    scope: zod_1.z.enum(['all', 'selected']),
    locationIds: zod_1.z.array(zod_1.z.string()),
    clearOverrides: zod_1.z.boolean(),
    scheduledAt: zod_1.z.string().nullable(),
    publishedAt: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    createdBy: zod_1.z.string().nullable(),
    writes: zod_1.z.array(exports.FuelPublishWriteSchema),
});
/** What a till receives for its location. */
exports.FuelPosGradeSchema = zod_1.z.object({
    gradeId: zod_1.z.string(),
    name: zod_1.z.string(),
    label: zod_1.z.string().optional(),
    color: zod_1.z.string(),
    sortOrder: zod_1.z.number(),
    priceMills: zod_1.z.number().int(),
});
exports.FuelPosConfigSchema = zod_1.z.object({
    enabled: zod_1.z.boolean(),
    locationId: zod_1.z.string(),
    grades: zod_1.z.array(exports.FuelPosGradeSchema),
    settings: exports.FuelSettingsSchema,
    promos: zod_1.z.array(exports.FuelPromoSchema),
    pricesUpdatedAt: zod_1.z.string().nullable(),
});
/** Fuel details carried on the sale line (cart → sale → receipt/reprint). */
exports.FuelLineInfoSchema = zod_1.z.object({
    gradeId: zod_1.z.string(),
    gradeName: zod_1.z.string(),
    pumpNumber: zod_1.z.number().int().nullable(),
    mode: exports.FuelEntryModeSchema,
    gallons: zod_1.z.number(),
    /** price actually charged per gallon, mills */
    priceMills: zod_1.z.number().int(),
    /** posted pump price before fuel discounts, mills */
    regularPriceMills: zod_1.z.number().int(),
    discountCentsPerGallon: zod_1.z.number().int(),
    promoNames: zod_1.z.array(zod_1.z.string()),
    /** true on an unused-prepay refund line */
    refund: zod_1.z.boolean().default(false),
});
// ─────────────────────────────── math ───────────────────────────────
/** Effective published price at a location: override → default. null = not sold. */
function resolveFuelPrice(defaultCell, locationCell) {
    if (locationCell?.hidden)
        return null;
    if (locationCell?.priceMills != null)
        return locationCell.priceMills;
    return defaultCell?.priceMills ?? null;
}
/** Cents-per-gallon discount that applies, and which promos gave it. */
function resolveFuelDiscount(promos, ctx, bestDiscountOnly) {
    const eligible = promos.filter((p) => p.active &&
        (p.gradeIds.length === 0 || p.gradeIds.includes(ctx.gradeId)) &&
        (p.locationIds.length === 0 || p.locationIds.includes(ctx.locationId)) &&
        (p.kind === 'everyone' || (p.kind === 'loyalty' && ctx.hasCustomer) || (p.kind === 'cash' && ctx.isCash)));
    if (eligible.length === 0)
        return { centsPerGallon: 0, promoNames: [] };
    if (bestDiscountOnly) {
        const best = eligible.reduce((a, b) => (b.centsPerGallon > a.centsPerGallon ? b : a));
        return { centsPerGallon: best.centsPerGallon, promoNames: [best.name] };
    }
    return {
        centsPerGallon: eligible.reduce((s, p) => s + p.centsPerGallon, 0),
        promoNames: eligible.map((p) => p.name),
    };
}
/**
 * Price one fuel line.
 *  - amount mode: the customer pays exactly `amountCents`; the discount buys more gallons.
 *  - gallons mode: the customer gets exactly `gallonsMilli`; the discount lowers the total.
 * Integer maths only. totalCents always equals round2(price × gallons), the same
 * figure the till's cart computes, so screen, cart and receipt agree.
 */
function computeFuelLine(input) {
    const priceMills = Math.max(1, input.regularPriceMills - input.discountCentsPerGallon * 10);
    let gallonsMilli;
    if (input.mode === 'amount') {
        const amountCents = Math.max(0, Math.round(input.amountCents ?? 0));
        // gallons = dollars / ($/gal) = (cents/100) / (mills/1000) → ×1000 for thousandths
        gallonsMilli = Math.round((amountCents * 10000) / priceMills);
    }
    else {
        gallonsMilli = Math.max(0, Math.round(input.gallonsMilli ?? 0));
    }
    // gallons × price in cents = (gm/1000) × (mills/1000) × 100 = gm × mills / 10 000
    const totalCents = Math.round((gallonsMilli * priceMills) / 10000);
    return { priceMills, gallonsMilli, totalCents };
}
function formatFuelPrice(mills) {
    return `$${(mills / 1000).toFixed(3)}`;
}
function formatGallons(gallonsMilli) {
    return (gallonsMilli / 1000).toFixed(3);
}
const defaultFuelSettings = () => exports.FuelSettingsSchema.parse({});
exports.defaultFuelSettings = defaultFuelSettings;
//# sourceMappingURL=fuel.schema.js.map